/**
 * Vendor risk scoring.
 *
 * THE SCALE
 * Every score in this module is 0–3, where 0 is "no risk" and 3 is "maximum
 * risk". One scale for all three inputs is what makes them combinable and what
 * makes the composite explainable to a board.
 *
 * THE THREE INPUTS
 *   Inherent risk  — a human judgement recorded on the vendor record, made
 *                    BEFORE any controls or mitigation. "How bad would it be
 *                    if this supplier failed?"
 *   Compliance risk — derived from the vendor's most recent completed
 *                    self-assessment questionnaire.
 *   Security risk  — derived from external vulnerability scan findings.
 *
 * WHY THE WEIGHTS ARE WHAT THEY ARE
 * Inherent risk is weighted highest because it is the only input that reflects
 * *consequence*: a vendor with perfect security that holds your entire customer
 * database is still your biggest exposure. The two measured inputs are weighted
 * equally — one is what the vendor tells you, the other is what you can observe
 * without asking, and neither deserves to dominate the other.
 *
 * MISSING INPUTS
 * An unmeasured input is NOT treated as zero. Scoring an unscanned vendor as
 * "no security risk" would reward never looking. Instead its weight is
 * redistributed across the inputs we do have, and `confidence` reports how much
 * of the model was actually backed by evidence — so the UI can say "based on 2
 * of 3 inputs" rather than implying false precision.
 */
/** Relative weight of each input in the composite. Must sum to 1. */
export const RISK_WEIGHTS = {
    inherent: 0.4,
    compliance: 0.3,
    security: 0.3,
};
/** The 0–3 scale's upper bound, named so nothing hard-codes a 3. */
export const RISK_SCALE_MAX = 3;
/** Maps the human-set inherent risk band onto the numeric scale. */
export const INHERENT_RISK_SCORE = {
    LOW: 0.5,
    MEDIUM: 1.5,
    HIGH: 2.25,
    CRITICAL: 3,
};
/**
 * Turn a 0–100 compliance rate into a 0–3 risk score.
 * Inverted: full compliance is no risk; zero compliance is maximum risk.
 */
export function complianceRateToRisk(rate0to100) {
    const clamped = Math.min(100, Math.max(0, rate0to100));
    return round2(((100 - clamped) / 100) * RISK_SCALE_MAX);
}
/**
 * Turn scan findings into a 0–3 security risk score.
 *
 * Red findings dominate by design: one critical exposure matters more than a
 * long tail of informational notes, so this is a severity-weighted ratio rather
 * than a simple count. A vendor with no findings at all scores 0; a vendor
 * whose findings are entirely red approaches 3.
 */
export function scanFindingsToRisk(red, amber, green) {
    const total = red + amber + green;
    if (total === 0)
        return 0;
    // Weighted severity mass over the maximum possible mass for that many findings.
    const mass = red * 1.0 + amber * 0.45 + green * 0.05;
    return round2(Math.min(RISK_SCALE_MAX, (mass / total) * RISK_SCALE_MAX));
}
/**
 * Combine the three inputs into a composite score.
 *
 * Weights of missing inputs are redistributed proportionally across the
 * remaining ones, so the composite is always on the same 0–3 scale and never
 * silently depressed by absent data.
 */
export function compositeRisk(inputs) {
    const present = [];
    present.push({ key: 'inherent', value: inputs.inherent, weight: RISK_WEIGHTS.inherent });
    if (inputs.compliance !== null) {
        present.push({ key: 'compliance', value: inputs.compliance, weight: RISK_WEIGHTS.compliance });
    }
    if (inputs.security !== null) {
        present.push({ key: 'security', value: inputs.security, weight: RISK_WEIGHTS.security });
    }
    const weightSum = present.reduce((s, p) => s + p.weight, 0);
    // weightSum can never be 0: inherent risk is always present.
    const score = round2(present.reduce((s, p) => s + p.value * (p.weight / weightSum), 0));
    return {
        score,
        confidence: round2(weightSum),
        contributions: present,
        band: bandFor(score),
    };
}
/**
 * Band a 0–3 score. Thresholds are deliberately not evenly spaced: the top band
 * is narrow so that "critical" stays rare enough to mean something.
 */
export function bandFor(score) {
    if (score >= 2.5)
        return 'CRITICAL';
    if (score >= 1.75)
        return 'HIGH';
    if (score >= 0.9)
        return 'MEDIUM';
    return 'LOW';
}
function round2(n) {
    return Math.round(n * 100) / 100;
}
//# sourceMappingURL=risk.js.map